<template>
  <div class="role-selector">
    <el-select
      :value="value"
      class="mb-4 dropdown"
      placeholder="เลือกตำแหน่ง"
      @input="handleChange"
    >
      <el-option
        v-for="role in roleOptions"
        :key="role.value"
        :label="role.label"
        :value="role.value"
      ></el-option>
    </el-select>
    <div>
      <div
        v-for="featureGroup in allFeatures"
        :key="featureGroup.group_label"
        class="feature-group"
      >
        <span class="group-label">{{ featureGroup.group_label }}</span>
        <div class="feature-list">
          <label
            v-for="feature in featureGroup.features"
            :key="feature.id"
            class="select-input readonly"
          >
            <input :checked="feature.checked" type="checkbox" disabled />
            <span class="checkmark checkbox"></span>
            <span>{{ feature.label }}</span>
          </label>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import * as R from "ramda"
import { Select, Option } from "element-ui"

export default {
  components: {
    "el-select": Select,
    "el-option": Option,
  },
  props: {
    value: {
      type: null,
      default: undefined,
    },
    roles: {
      type: null,
      default: undefined,
    },
  },
  computed: {
    roleOptions() {
      if (!this.roles) {
        return []
      }

      return this.roles.map((role) => {
        const label = `${role.role} - ${role.role_name}`
        const value = role.role_id

        return {
          label,
          value,
        }
      })
    },
    allFeatures() {
      if (!this.roles) {
        return []
      }

      const allFeaturesList = this.roles.flatMap((role) =>
        role.admin_features.map((subElement) => ({
          feature_id: subElement.feature_id,
          feature_code: subElement.feature_code,
          feature_group: subElement.feature_group,
        })),
      )

      const uniqueFeatures = [
        ...new Map(
          allFeaturesList.map((item) => [item.feature_id, item]),
        ).values(),
      ]

      const grouppedFeatures = R.groupBy(R.prop("feature_group"))(
        uniqueFeatures,
      )

      const groupOrders = this.getFeatureGroupOrders()

      const resultGroups = groupOrders
        .map((groupName) => {
          const groupItems = grouppedFeatures[groupName]

          if (!groupItems) {
            return null
          }

          const features = groupItems.map(this.pasreFeature)

          return {
            group_label: this.translateString(
              `feature.staff.access.${groupName.toLowerCase()}.group_name`,
              groupName,
            ),
            features,
          }
        })
        .filter(Boolean)

      return resultGroups
    },
    roleByIds() {
      if (!this.roles) {
        return {}
      }

      return Object.fromEntries(this.roles.map((role) => [role.role_id, role]))
    },
    selectedRole() {
      return this.roleByIds[this.value]
    },
    selectedFeatureIds() {
      if (!this.selectedRole) {
        return {}
      }

      return Object.fromEntries(
        this.selectedRole.admin_features.map(({ feature_id: featureId }) => [
          featureId,
          true,
        ]),
      )
    },
  },
  methods: {
    handleChange(value) {
      this.$emit("input", value)
    },
    pasreFeature(item) {
      const {
        feature_id: featureId,
        feature_code: featureCode,
        feature_group: featureGroup,
      } = item

      const fallbackLabel = featureCode.toLowerCase()
      const translationKey = `feature.staff.access.${featureGroup.toLowerCase()}.${fallbackLabel}`
      const label = this.translateString(translationKey, fallbackLabel)

      const checked = this.selectedFeatureIds[featureId]

      return {
        id: featureId,
        checked,
        label,
      }
    },
    translateString(key, fallback) {
      const messageValue = this.getLocaleMessageValue(key)

      if (messageValue !== undefined) {
        return typeof messageValue === "string" ? messageValue : fallback
      }

      if (this.$te && !this.$te(key)) {
        return fallback
      }

      const translated = this.$t(key)

      return typeof translated === "string" && translated !== key
        ? translated
        : fallback
    },
    getLocaleMessageValue(key) {
      if (!this.$i18n || !this.$i18n.getLocaleMessage) {
        return undefined
      }

      const messages = this.$i18n.getLocaleMessage(this.$i18n.locale)

      return key.split(".").reduce((current, path) => {
        if (
          current &&
          Object.prototype.hasOwnProperty.call(current, path)
        ) {
          return current[path]
        }

        return undefined
      }, messages)
    },
    getFeatureGroupOrders() {
      return [
        "NEWS",
        "SERVICE",
        "SUBSCRIPTION",
        "GROUP",
        "HOME_AUTOMATION",
        "EV_PARKING",
        "REFINANCE",
        "SELL_AND_RENT",
        "INSURANCE",
        "MONITORING",
        "RUEJAI_CLUB",
        "JURISTIC_SYSTEM",
        "SAP",
        "REWARD_STORE",
        "MNC",
        "CONFERENCE",
        "SUPER_ADMIN",
        "COBE_COMMUNITY",
      ]
    },
  },
}
</script>

<style lang="scss" scoped>
.select-input.readonly {
  cursor: auto;

  input[type="checkbox"] ~ span {
    opacity: 0.3;
  }

  input[type="checkbox"]:checked ~ span {
    opacity: 1;
  }

  .checkmark {
    border-color: transparent;
    background: none;

    &:after {
      border-color: $orange;
    }

    &:hover {
      background: none;
    }
  }
}

.dropdown {
  width: 100%;
}

.feature-group {
  margin-bottom: 10px;
}

.group-label {
  font-weight: bold;
  font-size: 18px;
}

.feature-list {
  margin-top: 8px;
  display: grid;
  grid-template-columns: repeat(
    auto-fill,
    minmax(200px, 1fr)
  ); // Adjust columns to wrap content with min width
  grid-template-rows: repeat(5, auto); // Limit the number of rows to 5
  grid-auto-flow: column; // Automatically flow items into columns
}
</style>
